import './ReportPreview.scss';

import { IconDownload } from '@hulu-react-style-components/icons';
import { useFormikContext } from 'formik';
import { bem } from 'mission-control-common-components';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';

import { useInfiniteScroll } from '../../../../../../hooks/useInfiniteScroll';
import { REPORT_LINE_ITEM_LIST_PAGE_SIZE } from '../../constants';
import type { SetCPScheduleFormValues } from '../../types';
import { CpScheduleFormFields } from '../../types';
import useReportExport from './hooks/useReportExport';
import ReportPreviewTable from './ReportPreviewTable';

const [block, element] = bem('report-preview');

const ReportPreview = (): React.JSX.Element => {
  const { values } = useFormikContext<SetCPScheduleFormValues>();

  const [visibleCount, setVisibleCount] = useState(REPORT_LINE_ITEM_LIST_PAGE_SIZE);

  const lineItemList = values[CpScheduleFormFields.LineItemList];
  const windowsCount = values[CpScheduleFormFields.Summary]?.windows.length ?? 0;

  const visibleItems = useMemo(() => lineItemList.slice(0, visibleCount), [lineItemList, visibleCount]);
  const hasMore = visibleCount < lineItemList.length;

  const { csvFullExportLink, dataToExport, onClickExport, exportFileName } = useReportExport({
    windowsCount,
    lineItemList,
  });

  const fetchMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + REPORT_LINE_ITEM_LIST_PAGE_SIZE, lineItemList.length));
  }, [lineItemList.length]);

  const { triggerRef } = useInfiniteScroll<HTMLTableRowElement>({ hasMore, loading: false, fetchMore });

  useEffect(() => {
    setVisibleCount(REPORT_LINE_ITEM_LIST_PAGE_SIZE);
  }, [lineItemList]);

  return (
    <div className={block()}>
      <div className={element('table-action-bar')}>
        {lineItemList.length > 0 && (
          <button className={element('download-button')} onClick={onClickExport}>
            <IconDownload />
            <span>Export</span>
          </button>
        )}
        {dataToExport.length > 1 && (
          <CSVLink
            role="link"
            filename={exportFileName}
            data={dataToExport}
            className={element('csv-link')}
            ref={csvFullExportLink}
          />
        )}
      </div>
      <div className={element('table-wrapper')}>
        <ReportPreviewTable windowsCount={windowsCount} lineItemList={visibleItems} loadMoreTriggerRef={triggerRef} />
      </div>
    </div>
  );
};

export default ReportPreview;
